import { memo, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Markup, { bodyClass } from "./routemobilemarkup";
import { usePage } from "../hooks/usePage";

import {
    fetchRouteMobiles,
    fetchRouteMobileById,
    createRouteMobile,
    editRouteMobile,
    clearRouteMobileError,
} from "../store/slices/routemobile";

import { getOfficers } from "../store/slices/user";
import { getBooths } from "../store/slices/booth";

const PageMarkup = memo(Markup);

const initialForm = {
    name: "",
    siId: "",
    users: [],
    boothIds: [],
};

const getId = (value) => {
    if (!value) return "";
    return typeof value === "object" ? value._id || "" : value;
};

const normalizeRouteMobile = (data) => {
    if (!data) return null;

    return (
        data.routeMobile ||
        data.data?.routeMobile ||
        data.data ||
        data
    );
};

export default function RouteMobiles() {
    usePage({
        title: "Route Mobile Management - Nellore Police Dashboard",
        bodyClass,
    });

    const dispatch = useDispatch();

    const {
        routeMobiles = [],
        loading,
        submitting,
        error,
    } = useSelector((state) => state.routeMobile);

    const { officers = [] } = useSelector(
        (state) => state.user
    );

    const { booths = [] } = useSelector(
        (state) => state.booth
    );

    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("ALL");
    const [viewMode, setViewMode] = useState("grid");

    const [showModal, setShowModal] = useState(false);
    const [editingRouteMobileId, setEditingRouteMobileId] =
        useState(null);

    const [detailsLoading, setDetailsLoading] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [formError, setFormError] = useState("");

    // --------------------------------------------------
    // INITIAL DATA
    // --------------------------------------------------

    const loadData = () => {
        dispatch(fetchRouteMobiles());

        dispatch(
            getOfficers({
                active: true,
                page: 1,
                limit: 1000,
            })
        );

        dispatch(
            getBooths({
                page: 1,
                limit: 1000,
            })
        );
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // --------------------------------------------------
    // FILTER DATA
    // --------------------------------------------------

    const filteredRouteMobiles = useMemo(() => {
        return routeMobiles.filter((item) => {
            const searchText = search.trim().toLowerCase();

            const siName =
                item.siId?.displayName ||
                item.siId?.name ||
                "";

            const matchesSearch =
                !searchText ||
                [
                    item.name,
                    siName,
                    item.siId?.officerId,
                ].some((value) =>
                    String(value || "")
                        .toLowerCase()
                        .includes(searchText)
                );

            const matchesStatus =
                status === "ALL" ||
                String(item.status || "ACTIVE").toUpperCase() ===
                status;

            return matchesSearch && matchesStatus;
        });
    }, [routeMobiles, search, status]);

    // --------------------------------------------------
    // STATISTICS
    // --------------------------------------------------

    const statistics = useMemo(
        () => ({
            total: routeMobiles.length,

            active: routeMobiles.filter(
                (item) =>
                    String(item.status || "ACTIVE").toUpperCase() ===
                    "ACTIVE"
            ).length,

            inactive: routeMobiles.filter(
                (item) =>
                    String(item.status || "").toUpperCase() ===
                    "INACTIVE"
            ).length,

            totalUsers: routeMobiles.reduce(
                (total, item) =>
                    total + (item.users?.length || 0),
                0
            ),

            totalBooths: routeMobiles.reduce(
                (total, item) =>
                    total + (item.boothIds?.length || 0),
                0
            ),
        }),
        [routeMobiles]
    );

    // --------------------------------------------------
    // MODAL
    // --------------------------------------------------

    const openAddModal = () => {
        dispatch(clearRouteMobileError());

        setEditingRouteMobileId(null);
        setForm(initialForm);
        setFormError("");
        setShowModal(true);
    };

    const openEditModal = async (routeMobile) => {
        dispatch(clearRouteMobileError());

        setEditingRouteMobileId(routeMobile._id);
        setForm(initialForm);
        setFormError("");
        setShowModal(true);
        setDetailsLoading(true);

        try {
            const response = await dispatch(
                fetchRouteMobileById(routeMobile._id)
            ).unwrap();

            const selectedRouteMobile =
                normalizeRouteMobile(response);

            if (!selectedRouteMobile?._id) {
                throw new Error("Route Mobile details not found");
            }

            setForm({
                name: selectedRouteMobile.name || "",

                siId: getId(selectedRouteMobile.siId),

                users: Array.isArray(selectedRouteMobile.users)
                    ? selectedRouteMobile.users
                        .map((item) => getId(item))
                        .filter(Boolean)
                    : [],

                boothIds: Array.isArray(selectedRouteMobile.boothIds)
                    ? selectedRouteMobile.boothIds
                        .map((item) => getId(item))
                        .filter(Boolean)
                    : [],
            });
        } catch (err) {
            console.error("Route Mobile edit error:", err);

            setFormError(
                typeof err === "string"
                    ? err
                    : err?.message || "Failed to load Route Mobile details"
            );
        } finally {
            setDetailsLoading(false);
        }
    };

    const closeModal = () => {
        if (submitting) return;

        setShowModal(false);
        setEditingRouteMobileId(null);
        setForm(initialForm);
        setFormError("");
    };

    // --------------------------------------------------
    // FORM HANDLERS
    // --------------------------------------------------

    const handleFormChange = (field, value) => {
        setForm((previous) => ({
            ...previous,
            [field]: value,
        }));
    };

    const handleUserToggle = (userId, checked) => {
        setForm((previous) => ({
            ...previous,
            users: checked
                ? [...new Set([...previous.users, userId])]
                : previous.users.filter((id) => id !== userId),
        }));
    };

    const handleBoothToggle = (boothId, checked) => {
        setForm((previous) => ({
            ...previous,
            boothIds: checked
                ? [...new Set([...previous.boothIds, boothId])]
                : previous.boothIds.filter((id) => id !== boothId),
        }));
    };

    // --------------------------------------------------
    // CREATE / UPDATE
    // --------------------------------------------------

    const handleSubmit = async (event) => {
        event?.preventDefault();

        setFormError("");

        if (!form.name.trim()) {
            setFormError("Please enter Route Mobile name");
            return;
        }

        if (!form.siId) {
            setFormError("Please select SI / In-charge");
            return;
        }

        const payload = {
            name: form.name.trim(),
            siId: form.siId,
            users: form.users,
            boothIds: form.boothIds,
        };

        try {
            if (editingRouteMobileId) {
                await dispatch(
                    editRouteMobile({
                        id: editingRouteMobileId,
                        data: payload,
                    })
                ).unwrap();
            } else {
                await dispatch(
                    createRouteMobile(payload)
                ).unwrap();
            }

            setShowModal(false);
            setEditingRouteMobileId(null);
            setForm(initialForm);
            setFormError("");

            dispatch(fetchRouteMobiles());
        } catch (err) {
            setFormError(
                typeof err === "string"
                    ? err
                    : err?.message || "Failed to save Route Mobile"
            );
        }
    };

    // --------------------------------------------------
    // RENDER
    // --------------------------------------------------

    return (
        <PageMarkup
            routeMobiles={filteredRouteMobiles}
            totalRouteMobiles={routeMobiles.length}
            statistics={statistics}
            loading={loading}
            submitting={submitting}
            detailsLoading={detailsLoading}
            error={error}
            search={search}
            setSearch={setSearch}
            status={status}
            setStatus={setStatus}
            viewMode={viewMode}
            setViewMode={setViewMode}
            showModal={showModal}
            editingRouteMobileId={editingRouteMobileId}
            form={form}
            formError={formError}
            officers={officers}
            booths={booths}
            onAddRouteMobile={openAddModal}
            onEditRouteMobile={openEditModal}
            onCloseModal={closeModal}
            onFormChange={handleFormChange}
            onUserToggle={handleUserToggle}
            onBoothToggle={handleBoothToggle}
            onSubmit={handleSubmit}
        />
    );
}